import { useState, useEffect } from 'react'
import {
  XMarkIcon,
  FolderOpenIcon,
  PencilSquareIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import {
  FolderService,
  type SharedFolder,
} from '../../../../shared/services/folderService'
import { useTranslation } from 'react-i18next'

interface SharedFoldersModalProps {
  onClose: () => void
}

function SharedFoldersModal({ onClose }: SharedFoldersModalProps) {
  const { t } = useTranslation()
  const [folders, setFolders] = useState<SharedFolder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        setLoading(true)
        const res = await FolderService.getSharedFolders()
        if (!active) {
          return
        }
        if (res?.success) {
          setFolders(res.folders ?? [])
        } else {
          setError(res?.message || t('form.sharedLoadError'))
        }
      } catch (err) {
        if (active) {
          setError(t('form.sharedLoadError'))
        }
        console.error('공유 폴더 로드 실패:', err)
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }
    load()
    return () => {
      active = false
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-100 p-6 dark:border-gray-700">
          <h2 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
            <FolderOpenIcon className="mr-2 h-5 w-5 text-blue-600" />팀 공유
            폴더
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {t('form.loading')}
            </p>
          ) : error ? (
            <p className="py-8 text-center text-sm text-red-600">{error}</p>
          ) : folders.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {t('form.noSharedFolders')}
            </p>
          ) : (
            <ul className="space-y-3">
              {folders.map(folder => (
                <li
                  key={folder._id}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${folder.color}20` }}
                  >
                    <FolderOpenIcon
                      className="h-5 w-5"
                      style={{ color: folder.color }}
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 dark:text-white">
                      {folder.name}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {folder.team?.name} ·{' '}
                      {folder.owner?.username || t('form.unknown')} · 링크{' '}
                      {folder.linkCount}개
                    </p>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                      folder.canEdit
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {folder.canEdit ? (
                      <>
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                        편집
                      </>
                    ) : (
                      <>
                        <EyeIcon className="h-3.5 w-3.5" />
                        보기
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default SharedFoldersModal
